import { useCallback, useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, Download, ArrowUpRight, ArrowDownRight, ExternalLink, FilterX } from "lucide-react";

const TIPO_LABEL = {
  ENTRADA_COMPRA: "Entrada por compra",
  AJUSTE_ESTOQUE: "Ajuste de estoque",
  RESERVA_SEPARACAO: "Reserva (separação)",
  EXPEDICAO_CD: "Expedição (CD)",
  EM_TRANSITO: "Transferência em trânsito",
  RECEBIMENTO_TRANSITO: "Baixa de trânsito",
  RECEBIMENTO_CREDITO: "Recebimento (unidade)",
  ENTRADA_DEVOLUCAO: "Entrada por devolução",
  SAIDA_DEVOLUCAO: "Saída por devolução",
  AJUSTE_INVENTARIO: "Ajuste de inventário",
  SAIDA_ESTOQUE: "Saída de estoque",
  ESTORNO_SAIDA: "Estorno de saída",
};
const tipoLabel = (t) => TIPO_LABEL[t] || t;
const CAMPO_LABEL = { em_transito: "em trânsito", reservado: "reservado" };
const campoTag = (c) => c && c !== "disponivel" ? CAMPO_LABEL[c] : null;
const fmtDT = (s) => { try { return new Date(s).toLocaleString("pt-BR"); } catch { return s; } };
const money = (v) => v == null ? "—" : `R$ ${Number(v).toFixed(2)}`;

export default function HistoricoEstoque({ produtoId, onClose }) {
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [f, setF] = useState({ de: "", ate: "", tipo: "", unit_id: "", usuario_id: "" });

  const load = useCallback(() => {
    setLoading(true);
    const params = {};
    Object.entries(f).forEach(([k, v]) => { if (v) params[k] = v; });
    api.get(`/produtos/${produtoId}/historico`, { params }).then((r) => { setData(r.data); setLoading(false); });
  }, [produtoId, f]);
  useEffect(() => { if (produtoId) load(); }, [produtoId, load]);

  const movimentos = data?.movimentos || [];
  const canFin = data?.can_finance;

  const goRef = (ref) => { if (ref?.rota) { onClose(); nav(ref.rota); } };
  const limpar = () => setF({ de: "", ate: "", tipo: "", unit_id: "", usuario_id: "" });

  const exportCSV = () => {
    const head = ["Data/Hora", "Tipo", "Qtd", "Saldo antes", "Saldo depois", "Local", "Origem", "Destino", "Responsável", "Referência", "Fornecedor", "Motivo"];
    if (canFin) head.push("Custo un.", "Custo total");
    const rows = movimentos.map((m) => {
      const base = [fmtDT(m.timestamp), tipoLabel(m.tipo), m.quantidade, m.saldo_antes, m.saldo_depois, m.unidade,
        m.origem || "", m.destino || "", m.usuario_nome || "", m.ref?.numero || "", m.fornecedor || "", (m.motivo || "").replace(/;/g, ",")];
      if (canFin) base.push(m.custo_unitario ?? "", m.custo_total ?? "");
      return base.join(";");
    });
    const csv = [head.join(";"), ...rows].join("\n");
    const blob = new Blob(["\ufeff" + csv], { type: "text/csv;charset=utf-8;" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `historico_${data?.produto?.sku || "produto"}.csv`;
    a.click();
  };

  const Qtd = ({ q }) => (
    <span className={`inline-flex items-center gap-0.5 font-mono font-bold ${q > 0 ? "text-emerald-300" : q < 0 ? "text-red-300" : "text-muted-foreground"}`}>
      {q > 0 ? <ArrowUpRight className="w-3.5 h-3.5" /> : q < 0 ? <ArrowDownRight className="w-3.5 h-3.5" /> : null}
      {q > 0 ? `+${q}` : q}
    </span>
  );

  return (
    <Dialog open={!!produtoId} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-5xl max-h-[92vh] overflow-y-auto" data-testid="historico-modal">
        <DialogHeader>
          <DialogTitle className="font-display uppercase">Histórico de Movimentações</DialogTitle>
          <DialogDescription>
            {data ? `${data.produto.nome} · ${data.produto.sku}` : "Carregando…"} — extrato cronológico do estoque.
          </DialogDescription>
        </DialogHeader>

        {/* Filtros */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-2 items-end">
          <div><Label className="text-[10px]">De</Label><Input type="date" value={f.de} onChange={(e) => setF((x) => ({ ...x, de: e.target.value }))} data-testid="hist-de" /></div>
          <div><Label className="text-[10px]">Até</Label><Input type="date" value={f.ate} onChange={(e) => setF((x) => ({ ...x, ate: e.target.value }))} data-testid="hist-ate" /></div>
          <div><Label className="text-[10px]">Tipo</Label>
            <Select value={f.tipo || "ALL"} onValueChange={(v) => setF((x) => ({ ...x, tipo: v === "ALL" ? "" : v }))}>
              <SelectTrigger data-testid="hist-tipo"><SelectValue placeholder="Todos" /></SelectTrigger>
              <SelectContent><SelectItem value="ALL">Todos</SelectItem>{(data?.filtros.tipos || []).map((t) => <SelectItem key={t} value={t}>{tipoLabel(t)}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label className="text-[10px]">CD / Unidade</Label>
            <Select value={f.unit_id || "ALL"} onValueChange={(v) => setF((x) => ({ ...x, unit_id: v === "ALL" ? "" : v }))}>
              <SelectTrigger data-testid="hist-unidade"><SelectValue placeholder="Todas" /></SelectTrigger>
              <SelectContent><SelectItem value="ALL">Todas</SelectItem>{(data?.filtros.unidades || []).map((u) => <SelectItem key={u.id} value={u.id}>{u.nome}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label className="text-[10px]">Responsável</Label>
            <Select value={f.usuario_id || "ALL"} onValueChange={(v) => setF((x) => ({ ...x, usuario_id: v === "ALL" ? "" : v }))}>
              <SelectTrigger data-testid="hist-resp"><SelectValue placeholder="Todos" /></SelectTrigger>
              <SelectContent><SelectItem value="ALL">Todos</SelectItem>{(data?.filtros.responsaveis || []).map((r) => <SelectItem key={r.id} value={r.id}>{r.nome}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        </div>
        <div className="flex items-center justify-between gap-2">
          <Button variant="ghost" size="sm" onClick={limpar} data-testid="hist-limpar"><FilterX className="w-4 h-4" /> Limpar filtros</Button>
          <Button variant="outline" size="sm" onClick={exportCSV} disabled={!movimentos.length} data-testid="hist-export"><Download className="w-4 h-4" /> Exportar CSV</Button>
        </div>

        {loading ? (
          <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>
        ) : !movimentos.length ? (
          <div className="py-16 text-center text-muted-foreground text-sm">Nenhuma movimentação para os filtros selecionados.</div>
        ) : (<>
          {/* Desktop: tabela */}
          <div className="hidden md:block overflow-x-auto rounded-md border border-border">
            <table className="w-full text-sm">
              <thead className="bg-secondary/60 text-[11px] uppercase text-muted-foreground">
                <tr>
                  <th className="text-left p-2">Data/Hora</th><th className="text-left p-2">Tipo</th>
                  <th className="text-right p-2">Qtd</th><th className="text-right p-2">Antes</th><th className="text-right p-2">Depois</th>
                  <th className="text-left p-2">Local</th><th className="text-left p-2">Responsável</th><th className="text-left p-2">Ref.</th>
                  {canFin && <th className="text-right p-2">Custo un.</th>}{canFin && <th className="text-right p-2">Custo total</th>}
                </tr>
              </thead>
              <tbody>
                {movimentos.map((m) => (
                  <tr key={m.id} className="border-t border-border hover:bg-secondary/30" data-testid={`hist-row-${m.id}`}>
                    <td className="p-2 whitespace-nowrap text-xs">{fmtDT(m.timestamp)}</td>
                    <td className="p-2">{tipoLabel(m.tipo)}{campoTag(m.campo) ? <span className="ml-1 text-[9px] uppercase font-bold px-1 py-0.5 rounded-sm bg-secondary text-muted-foreground">{campoTag(m.campo)}</span> : null}{m.fornecedor ? <span className="block text-[10px] text-muted-foreground">Forn.: {m.fornecedor}</span> : null}{m.motivo ? <span className="block text-[10px] text-muted-foreground truncate max-w-[220px]">{m.motivo}</span> : null}</td>
                    <td className="p-2 text-right"><Qtd q={m.quantidade} /></td>
                    <td className="p-2 text-right font-mono">{m.saldo_antes}</td>
                    <td className="p-2 text-right font-mono font-bold">{m.saldo_depois}</td>
                    <td className="p-2 text-xs">{m.unidade}{m.origem && m.destino ? <span className="block text-[10px] text-muted-foreground">{m.origem} → {m.destino}</span> : null}</td>
                    <td className="p-2 text-xs">{m.usuario_nome || "—"}</td>
                    <td className="p-2">{m.ref ? <button className="text-primary inline-flex items-center gap-1 text-xs font-mono" onClick={() => goRef(m.ref)} data-testid={`hist-ref-${m.id}`}>{m.ref.numero}<ExternalLink className="w-3 h-3" /></button> : "—"}</td>
                    {canFin && <td className="p-2 text-right font-mono text-xs">{money(m.custo_unitario)}</td>}
                    {canFin && <td className="p-2 text-right font-mono text-xs">{money(m.custo_total)}</td>}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {/* Mobile: cards */}
          <div className="md:hidden space-y-2">
            {movimentos.map((m) => (
              <div key={m.id} className="rounded-md border border-border bg-card/60 p-3" data-testid={`hist-card-${m.id}`}>
                <div className="flex items-center justify-between gap-2">
                  <span className="text-sm font-semibold">{tipoLabel(m.tipo)}{campoTag(m.campo) ? <span className="ml-1 text-[9px] uppercase font-bold px-1 py-0.5 rounded-sm bg-secondary text-muted-foreground">{campoTag(m.campo)}</span> : null}</span>
                  <Qtd q={m.quantidade} />
                </div>
                <div className="text-[11px] text-muted-foreground mt-0.5">{fmtDT(m.timestamp)} · {m.unidade}</div>
                <div className="flex items-center gap-3 mt-1 text-xs font-mono">
                  <span className="text-muted-foreground">Antes {m.saldo_antes}</span>
                  <span className="text-primary">→</span>
                  <span className="font-bold">Depois {m.saldo_depois}</span>
                </div>
                {m.origem && m.destino && <div className="text-[11px] text-muted-foreground mt-0.5">{m.origem} → {m.destino}</div>}
                {m.fornecedor && <div className="text-[11px] text-muted-foreground">Fornecedor: {m.fornecedor}</div>}
                <div className="flex items-center justify-between mt-1">
                  <span className="text-[11px] text-muted-foreground">{m.usuario_nome || "—"}</span>
                  {m.ref && <button className="text-primary inline-flex items-center gap-1 text-xs font-mono" onClick={() => goRef(m.ref)}>{m.ref.numero}<ExternalLink className="w-3 h-3" /></button>}
                </div>
                {canFin && (m.custo_unitario != null) && <div className="text-[11px] text-muted-foreground mt-0.5">Custo un. {money(m.custo_unitario)} · Total {money(m.custo_total)}</div>}
                {m.motivo && <div className="text-[11px] text-muted-foreground mt-0.5 italic">{m.motivo}</div>}
              </div>
            ))}
          </div>
        </>)}
      </DialogContent>
    </Dialog>
  );
}
